import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
} from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from 'api/axios';
import Listtable from 'components/common/ListTable';
import CghsRateModal from "./CghsRateModal";
import PanelModal from "./PanelModal";
import EditIcon from "@mui/icons-material/Edit";
import { useOutletContext } from "react-router-dom";

const CghsRateList = () => {
  const [activeTab, setActiveTab] = useState("cghs");
  const [count, setCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [, setBackDropOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [isUploading, setIsUploading] = useState(false);

  // CGHS States
  const [cghsData, setCghsData] = useState([]);
  const [cghsList, setCghsList] = useState([]);

  // Panel States
  const [panelData, setPanelData] = useState([]);
  const [panelList, setPanelList] = useState([]);

  const { searchText } = useOutletContext();

  // CGHS Table
  const tableHeader = [
    "Procedure Name",
    "CGHS Code",
    "Classification",
    "Base Rate (₹)",
    "Status",
    "Action",
  ];
  const keysName = [
    "procedureName",
    "cghsCode",
    "specialityClassification",
    "rateDisplay",
    "status",
    "action",
  ];

  // Panel Table
  const tableHeaderPanel = ["Panel Name", "Scheme", "Module", "Status", "Action"];
  const keysNamePanel = ["name", "schemeDisplay", "module", "status", "action"];

  const btnStyle = {
    width: "max-content",
    padding: "0.5rem 2rem",
    color: "#194AF5",
    bgcolor: "#E6EBFF",
    boxShadow: "none",
    borderRadius: "8px",
    ":hover": {
      bgcolor: "#E6EBFF",
      color: "#194AF5",
      boxShadow: "none",
    },
  };

  // Search filter
  useEffect(() => {
    if (activeTab === "cghs") {
      const filtered = cghsList?.filter(
        (x) =>
          x?.procedureName?.toLowerCase()?.includes(searchText?.toLowerCase()) ||
          x?.cghsCode?.toLowerCase()?.includes(searchText?.toLowerCase())
      );
      setCghsData(filtered);
    } else {
      const filtered = panelList?.filter((x) =>
        x?.name?.toLowerCase()?.includes(searchText?.toLowerCase())
      );
      setPanelData(filtered);
    }
  }, [searchText, cghsList, panelList, activeTab]);

  // CGHS Fetch
  const fetchCghsRates = () => {
    try {
      setBackDropOpen(true);
      api
        .post("/admin/cghs-rate-list", { limit: rowsPerPage, page: pageNumber + 1 })
        .then((response) => {
          const listData = response?.data?.data?.cghsList || [];
          const normalizedList = listData.map((item) => ({
            ...item,
            specialityClassification: item?.specialityClassification || "—",
            rateDisplay:
              item?.baseRate != null
                ? `₹${item.baseRate}`
                : "Unlisted (not configured)",
            action: (
              <IconButton onClick={() => handleModal(item)}>
                <EditIcon fontSize="small" color="primary" />
              </IconButton>
            ),
          }));
          setCghsData(normalizedList);
          setCghsList(normalizedList);
          setCount(response?.data?.data?.totalCount || normalizedList.length);
          setBackDropOpen(false);
        })
        .catch((err) => {
          setBackDropOpen(false);
          toast.error(err?.response?.data?.message || "CGHS rates fetch failed!");
        });
    } catch (error) {
      setBackDropOpen(false);
      toast.error("CGHS rates list error");
    }
  };

  // Panel Fetch
  const fetchPanels = () => {
    try {
      setBackDropOpen(true);
      api
        .post("/admin-panel/list", { limit: rowsPerPage, page: pageNumber + 1 })
        .then((response) => {
          const listData = response?.data?.data?.panels || [];
          const normalized = listData.map((item) => ({
            ...item,
            module: item.module || "OPD",
            schemeDisplay: item.schemeType || "—",
            action: (
              <IconButton onClick={() => handleModal(item)}>
                <EditIcon fontSize="small" color="primary" />
              </IconButton>
            ),
          }));
          setPanelData(normalized);
          setPanelList(normalized);
          setCount(response?.data?.data?.totalCount || normalized.length);
          setBackDropOpen(false);
        })
        .catch((err) => {
          setBackDropOpen(false);
          toast.error(err?.response?.data?.message || "Panels fetch failed!");
        });
    } catch (error) {
      setBackDropOpen(false);
      toast.error("Panels list error");
    }
  };

  const handleChangePage = (event, newPage) => setPageNumber(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPageNumber(0);
  };

  const handleModal = (data) => {
    setSelectedData(data);
    setOpenModal(true);
  };

  const handleModalClose = (buttonText = "save") => {
    setSelectedData({});
    if (buttonText !== "close") {
      activeTab === "cghs" ? fetchCghsRates() : fetchPanels();
    }
    setOpenModal(false);
  };

  const handleBulkUpload = (e) => {
    try {
      if (e?.target?.files?.length > 0) {
        setIsUploading(true);
        const fileData = e?.target?.files[0];
        const formData = new FormData();
        formData.append("cghs", fileData);
        api
          .post("/admin/add-cghs-rates-bulk", formData, {
            "Content-Type": "multipart/form-data",
          })
          .then((res) => {
            const result = res?.data || {};
            toast.success(result.message || "Bulk upload successful!");
            if (result.unlisted || result.rejected?.length) {
              toast.info(
                `${result.unlisted || 0} unlisted (no rate), ` +
                  `${result.rejected?.length || 0} rejected row(s), ` +
                  `${result.skippedNonDental || 0} non-dental row(s) skipped.`
              );
            }
            setRowsPerPage(5);
            setPageNumber(0);
            setIsUploading(false);
            fetchCghsRates();
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || "Bulk upload failed!");
            setIsUploading(false);
          });
        e.target.value = null;
      } else {
        e.target.value = null;
        toast.error("Please select a file!");
      }
    } catch (err) {
      setIsUploading(false);
      toast.error("Bulk upload failed!");
    }
  };

  const handleUploadButtonClick = () => {
    document.getElementById("cghs-file-upload").click();
  };

  useEffect(() => {
    setPageNumber(0);
    setCount(0);
    if (activeTab === "cghs") {
      fetchCghsRates();
    } else {
      fetchPanels();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "cghs") {
      fetchCghsRates();
    } else {
      fetchPanels();
    }
  }, [rowsPerPage, pageNumber]);

  return (
    <>
      <Grid container alignItems="center">
        <Grid item sm={12} md={12} lg={12} padding={2}>
          {/* Header Row */}
          <Grid
            container
            spacing={1}
            sx={{ padding: "1rem 0" }}
            alignItems="center"
            justifyContent="space-between"
          >
            {/* Tabs */}
            <Box sx={{ display: "flex", alignItems: "center", mr: "auto" }}>
              {/* CGHS Tab */}
              <Box
                onClick={() => setActiveTab("cghs")}
                sx={{
                  borderBottom: activeTab === "cghs" ? "2px solid #194AF5" : "2px solid transparent",
                  color: activeTab === "cghs" ? "#194AF5" : "#7D8DC4",
                  width: "max-content",
                  padding: "0.5rem 1rem",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                CGHS Rates
              </Box>

              {/* Panel Tab */}
              <Box
                onClick={() => setActiveTab("panel")}
                sx={{
                  borderBottom: activeTab === "panel" ? "2px solid #194AF5" : "2px solid transparent",
                  color: activeTab === "panel" ? "#194AF5" : "#7D8DC4",
                  width: "max-content",
                  padding: "0.5rem 1rem",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Panel
              </Box>
            </Box>

            {/* Buttons */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              {/* Bulk Upload — sirf CGHS tab mein */}
              {activeTab === "cghs" && (
                <Button
                  sx={btnStyle}
                  variant="contained"
                  onClick={handleUploadButtonClick}
                  title="Upload the CGHS portal rate list for TIER I / NABH / SEMI PRIVATE WARD. Only dental codes are imported."
                >
                  {isUploading ? (
                    <CircularProgress sx={{ color: "#194AF5" }} size="1.5rem" />
                  ) : (
                    "Upload via Excel"
                  )}
                  <input
                    type="file"
                    accept=".xlsx,.csv"
                    id="cghs-file-upload"
                    multiple={false}
                    hidden
                    onChange={handleBulkUpload}
                  />
                </Button>
              )}

              {/* Add Button */}
              <Button
                sx={{
                  ...btnStyle,
                  bgcolor: "#194AF5",
                  color: "white",
                  ":hover": { bgcolor: "#194AF5" },
                }}
                variant="contained"
                onClick={() => handleModal({})}
              >
                {activeTab === "cghs" ? "Add CGHS Rate" : "Add Panel"}
              </Button>
            </Box>
          </Grid>

          {/* Table */}
          <Listtable
            tableheader={activeTab === "cghs" ? tableHeader : tableHeaderPanel}
            tablebody={activeTab === "cghs" ? cghsData : panelData}
            rowsPerPage={rowsPerPage}
            page={pageNumber}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalCount={count}
            dataKeyName={activeTab === "cghs" ? keysName : keysNamePanel}
            editHandle={handleModal}
            tag={activeTab}
          />
        </Grid>
      </Grid>

      {/* Modals */}
      {openModal && activeTab === "cghs" && (
        <CghsRateModal
          open={openModal}
          onClose={handleModalClose}
          data={selectedData}
        />
      )}
      {openModal && activeTab === "panel" && (
        <PanelModal
          open={openModal}
          onClose={handleModalClose}
          data={selectedData}
        />
      )}
    </>
  );
};

export default CghsRateList;